import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Drawer, Stepper } from 'barte-design-system';
import { useQueryClient } from '@tanstack/react-query';

import {
  useRegisterTerminalsBatch,
  useResolveTerminalNames,
  useConfigureTerminals,
  useUnconfigureTerminals,
} from '@/modules/backoffice/services/terminals';
import { BACKOFFICE_QUERIES } from '@/modules/backoffice/services/queries';

import { StepOperation, StepModel, StepTerminals, StepLinkConfig, StepConfirm, useStepLinkConfigData } from './steppers';
import { ConfirmActionModal } from '../../modal/confirm-action-modal';
import { readWorkbook, extractSerialsFromWorkbook, extractCsvBatchRows, MAX_SERIALS_PER_BATCH } from './terminal-operation-drawer.utils';

import styles from './terminal-operation-drawer.module.css';

const ALLOWED_EXTENSIONS = ['.csv', '.xlsx'];
const MAX_FILE_SIZE_BYTES = 3 * 1024 * 1024;

import type {
  DrawerStep,
  OperationType,
  OperationStatus,
  OperationResult,
  ISelectedModel,
  ICsvBatchRow,
  IResolveNamesResponse,
  ILinkAssignment,
  IPOSCompanyItem,
  TerminalOperationDrawerProps,
} from './terminal-operation-drawer.type';

export const TerminalOperationDrawer = ({
  isOpen,
  onClose,
  onToast,
  initialSerialNumbers,
}: TerminalOperationDrawerProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit: rhfHandleSubmit,
    reset: resetSerialForm,
    setFocus,
    formState: { errors: serialFormErrors },
  } = useForm<{ serialInput: string }>({
    defaultValues: { serialInput: '' },
    mode: 'onChange',
  });

  const [step, setStep] = useState<DrawerStep>('OPERATION');
  const [selectedOperation, setSelectedOperation] = useState<OperationType | null>(null);
  const [selectedModel, setSelectedModel] = useState<ISelectedModel | null>(null);
  const [selectedPayfac, setSelectedPayfac] = useState<IPOSCompanyItem | null>(null);
  const [linkAssignments, setLinkAssignments] = useState<ILinkAssignment[]>([]);

  const stepLinkConfigData = useStepLinkConfigData(selectedPayfac?.id);


  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvBatchRows, setCsvBatchRows] = useState<ICsvBatchRow[]>([]);
  const [resolvedNames, setResolvedNames] = useState<IResolveNamesResponse>({});
  const [isResolvingNames, setIsResolvingNames] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [operationStatus, setOperationStatus] = useState<OperationStatus>('idle');
  const [operationResult, setOperationResult] = useState<OperationResult | null>(null);
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);
  const [isProcessConfirmOpen, setIsProcessConfirmOpen] = useState(false);

  const queryClient = useQueryClient();
  const { mutateAsync: registerTerminalsBatch } = useRegisterTerminalsBatch();
  const { mutateAsync: resolveTerminalNamesMutation } = useResolveTerminalNames();
  const { mutateAsync: configureTerminals } = useConfigureTerminals();
  const { mutateAsync: unconfigureTerminals } = useUnconfigureTerminals();

  useEffect(() => {
    if (!isOpen) return;
    resetState();
  }, [isOpen]);

  useEffect(() => {
    if (step === 'TERMINALS') {
      setFocus('serialInput');
    }
  }, [step, isOpen]);

  const resetState = () => {
    setStep('OPERATION');
    setSelectedOperation(null);
    setSelectedModel(null);
    setSelectedPayfac(null);
    setLinkAssignments([]);
    resetSerialForm();
    setSerialNumbers(initialSerialNumbers ? [...initialSerialNumbers] : []);
    setCsvFile(null);
    setCsvBatchRows([]);
    setResolvedNames({});
    setIsResolvingNames(false);
    setIsDragging(false);
    setOperationStatus('idle');
    setOperationResult(null);
    setIsProcessConfirmOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const hasUnsavedProgress = selectedOperation !== null && operationStatus !== 'done';

  const handleClose = () => {
    if (operationStatus === 'loading') return;
    if (hasUnsavedProgress) {
      setIsExitConfirmOpen(true);
      return;
    }
    resetState();
    onClose();
  };

  const handleConfirmExit = () => {
    setIsExitConfirmOpen(false);
    resetState();
    onClose();
  };

  const handleAddSerial = rhfHandleSubmit((data) => {
    const value = data.serialInput.trim();
    if (!value) return;
    if (selectedOperation === 'UNLINK' || selectedOperation === 'LINK') {
      setCsvFile(null);
      setCsvBatchRows([]);
      setResolvedNames({});
    }
    setSerialNumbers((prev) => [...prev, value]);
    resetSerialForm({ serialInput: '' });
    setFocus('serialInput');
  });

  const handleRemoveSerial = (serial: string) => {
    setSerialNumbers((prev) => prev.filter((s) => s !== serial));
  };

  const isValidFileType = (file: File) => {
    const name = file.name.toLowerCase();
    return ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
  };

  const showFileSizeError = () => onToast({
    variant: 'error',
    title: t('backoffice.terminalsWizardErrorTitle'),
    description: t('backoffice.terminalsDrawerFileMaxSizeError'),
  });

  const showFileReadError = () => onToast({
    variant: 'error',
    title: t('backoffice.terminalsWizardErrorTitle'),
    description: t('backoffice.terminalsDrawerFileReadError'),
  });

  const addRegisterSerialsFromFile = async (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) { showFileSizeError(); return; }
    try {
      const workbook = await readWorkbook(file);
      const parsed = await extractSerialsFromWorkbook(workbook);
      let duplicateCount = 0;
      setSerialNumbers((prev) => {
        duplicateCount = parsed.filter((s) => prev.includes(s)).length;
        const next = [...prev];
        parsed.forEach((s) => { if (!next.includes(s)) next.push(s); });
        return next;
      });
      if (duplicateCount > 0) {
        onToast({
          variant: 'warning',
          title: t('backoffice.terminalsWizardWarningTitle'),
          description: t('backoffice.terminalsDrawerFileDuplicatesWarning', { count: duplicateCount }),
        });
      }
    } catch (err) {
      console.error('[addRegisterSerialsFromFile]', err);
      showFileReadError();
    }
  };

  const addCsvBatchFromFile = async (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) { showFileSizeError(); return; }
    try {
      const workbook = await readWorkbook(file);
      const rows = await extractCsvBatchRows(workbook);
      if (rows.length === 0) { showFileReadError(); return; }

      setCsvFile(file);
      setCsvBatchRows(rows);
      setSerialNumbers([]);
      setResolvedNames({});

      const payfacIds = [...new Set(rows.map((r) => r.payfacId).filter((id): id is number => id != null))];
      const sellerIds = [...new Set(rows.map((r) => r.sellerId).filter((id): id is number => id != null))];

      if (payfacIds.length > 0 || sellerIds.length > 0) {
        setIsResolvingNames(true);
        const result = await resolveTerminalNamesMutation({ payfacIds, sellerIds });
        setIsResolvingNames(false);
        if (result.success && result.data) setResolvedNames(result.data);
      }
    } catch (err) {
      console.error('[addCsvBatchFromFile]', err);
      setIsResolvingNames(false);
      showFileReadError();
    }
  };

  const addFileByOperation = async (file: File) => {
    if (selectedOperation === 'LINK' || selectedOperation === 'UNLINK') {
      await addCsvBatchFromFile(file);
    } else {
      await addRegisterSerialsFromFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    if (!isValidFileType(file)) {
      onToast({
        variant: 'error',
        title: t('backoffice.terminalsWizardErrorTitle'),
        description: t('backoffice.terminalsDrawerFileTypeError'),
      });
      return;
    }
    void addFileByOperation(file);
  };

  const handleRemoveCsvFile = () => {
    setCsvFile(null);
    setCsvBatchRows([]);
    setResolvedNames({});
    setSerialNumbers([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveCsvRow = (serialNumber: string) => {
    setCsvBatchRows((prev) => prev.filter((r) => r.serialNumber !== serialNumber));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isValidFileType(file)) {
      onToast({
        variant: 'error',
        title: t('backoffice.terminalsWizardErrorTitle'),
        description: t('backoffice.terminalsDrawerFileTypeError'),
      });
      return;
    }
    void addFileByOperation(file);
  };

  const handleConfirm = async () => {
    setStep('CONFIRM');
    setOperationStatus('loading');

    try {
      let response: { success: boolean; message: string; data?: OperationResult };

      if (selectedOperation === 'REGISTER') {
        if (!selectedModel) { setOperationStatus('error'); return; }
        response = await registerTerminalsBatch({
          model: selectedModel.id,
          serialNumbers,
        });
      } else if (selectedOperation === 'LINK' && csvFile) {
        const payfacId = csvBatchRows.find((r) => r.payfacId != null)?.payfacId;
        if (payfacId == null) { setOperationStatus('error'); return; }
        const sellerGroupMap = new Map<number | undefined, string[]>();
        for (const row of csvBatchRows) {
          const key = row.sellerId ?? undefined;
          if (!sellerGroupMap.has(key)) sellerGroupMap.set(key, []);
          sellerGroupMap.get(key)!.push(row.serialNumber);
        }
        const assignments = Array.from(sellerGroupMap.entries()).map(([sellerId, serialNumbers]) => ({
          sellerId,
          serialNumbers,
        }));
        response = await configureTerminals({ payfacId, assignments });
      } else if (selectedOperation === 'LINK') {
        if (!selectedPayfac) { setOperationStatus('error'); return; }
        const assignedSet = new Set(linkAssignments.flatMap((a) => a.serialNumbers));
        const unassigned = serialNumbers.filter((s) => !assignedSet.has(s));
        const finalAssignments: { sellerId?: number; serialNumbers: string[] }[] =
          linkAssignments.map((a) => ({
            sellerId: a.sellerId,
            serialNumbers: a.serialNumbers,
          }));
        if (unassigned.length > 0) {
          finalAssignments.push({ serialNumbers: unassigned });
        }
        response = await configureTerminals({
          payfacId: selectedPayfac.id,
          assignments: finalAssignments,
        });
      } else if (selectedOperation === 'UNLINK' && csvFile) {
        const csvSerials = csvBatchRows.map((r) => r.serialNumber);
        response = await unconfigureTerminals({ serialNumbers: csvSerials });
      } else if (selectedOperation === 'UNLINK') {
        response = await unconfigureTerminals({ serialNumbers });
      } else {
        setOperationStatus('error');
        return;
      }

      if (!response.success) {
        setOperationStatus('error');
        onToast({
          variant: 'error',
          title: t('backoffice.terminalsWizardErrorTitle'),
          description: response.message,
        });
        return;
      }

      setOperationStatus('done');

      const rawResult = response.data ?? null;
      if (selectedOperation === 'LINK' && rawResult && 'errors' in rawResult) {
        const configureResult = rawResult as import('@/modules/backoffice/types/terminal').IConfigureTerminalResponse;

        const payfacMap = Object.fromEntries((resolvedNames.payfacs ?? []).map((p) => [p.id, p.name]));
        const sellerMap = Object.fromEntries((resolvedNames.sellers ?? []).map((s) => [s.id, s.name]));

        const getIntendedPayfac = (serial: string) => {
          if (csvFile) {
            const row = csvBatchRows.find((r) => r.serialNumber === serial);
            return { id: row?.payfacId ?? null, name: row?.payfacId != null ? (payfacMap[row.payfacId] ?? null) : null };
          }
          return { id: selectedPayfac?.id ?? null, name: selectedPayfac?.name ?? null };
        };

        const getIntendedSeller = (serial: string) => {
          if (csvFile) {
            const row = csvBatchRows.find((r) => r.serialNumber === serial);
            return { id: row?.sellerId ?? null, name: row?.sellerId != null ? (sellerMap[row.sellerId] ?? null) : null };
          }
          const assignment = linkAssignments.find((a) => a.serialNumbers.includes(serial));
          return { id: assignment?.sellerId ?? null, name: assignment?.sellerName ?? null };
        };

        const enrichedErrors = configureResult.errors.map((item) => {
          const payfac = getIntendedPayfac(item.serialNumber);
          const seller = getIntendedSeller(item.serialNumber);
          return {
            ...item,
            actualPayfacId: item.actualPayfacId ?? payfac.id,
            actualPayfacName: item.actualPayfacName ?? payfac.name,
            actualSellerId: item.actualSellerId ?? seller.id,
            actualSellerName: item.actualSellerName ?? seller.name,
          };
        });

        setOperationResult({ ...configureResult, errors: enrichedErrors });
      } else {
        setOperationResult(rawResult);
      }

      await queryClient.invalidateQueries({ queryKey: [BACKOFFICE_QUERIES.TERMINALS] });
    } catch (err) {
      console.error('[handleConfirm]', err);
      setOperationStatus('error');
    }
  };

  const getPreviousStepFromTerminals = () => {
    if (selectedOperation === 'REGISTER') return 'MODEL' as DrawerStep;
    return 'OPERATION' as DrawerStep;
  };

  const getNextStepFromTerminals = (): DrawerStep => {
    if (selectedOperation === 'LINK' && !csvFile) return 'LINK_CONFIG';
    return 'CONFIRM';
  };

  const isTerminalsStepReady = () => {
    if (selectedOperation === 'REGISTER') {
      return serialNumbers.length > 0 && serialNumbers.length <= MAX_SERIALS_PER_BATCH;
    }
    if (selectedOperation === 'LINK') {
      const csvReady = csvBatchRows.length > 0 && !isResolvingNames;
      const manualReady = serialNumbers.length > 0 && serialNumbers.length <= MAX_SERIALS_PER_BATCH;
      return csvReady || manualReady;
    }
    if (selectedOperation === 'UNLINK') {
      const csvReady = csvBatchRows.length > 0 && !isResolvingNames;
      const manualReady = serialNumbers.length > 0 && serialNumbers.length <= MAX_SERIALS_PER_BATCH;
      return csvReady || manualReady;
    }
    return false;
  };

  const isLinkConfigStepReady = () => {
    if (!selectedPayfac) return false;
    return !linkAssignments.some((a) => a.serialNumbers.length === 0);
  };

  const getFooter = () => {
    if (step === 'OPERATION') {
      return undefined;
    }

    if (step === 'MODEL') {
      return {
        primaryButton: {
          children: t('backoffice.terminalsDrawerBackButton'),
          onClick: () => setStep('OPERATION'),
          variant: 'outline' as const,
        },
      };
    }

    if (step === 'TERMINALS') {
      const nextStep = getNextStepFromTerminals();
      const isGoingToConfirm = nextStep === 'CONFIRM';
      return {
        secondaryButton: {
          children: t('backoffice.terminalsDrawerBackButton'),
          onClick: () => setStep(getPreviousStepFromTerminals()),
          variant: 'outline' as const,
        },
        primaryButton: {
          children: isGoingToConfirm
            ? t('backoffice.terminalsDrawerConfirmOperationButton')
            : t('backoffice.terminalsDrawerNextButton'),
          onClick: isGoingToConfirm
            ? () => setIsProcessConfirmOpen(true)
            : () => setStep(nextStep),
          disabled: !isTerminalsStepReady(),
        },
      };
    }

    if (step === 'LINK_CONFIG') {
      return {
        secondaryButton: {
          children: t('backoffice.terminalsDrawerBackButton'),
          onClick: () => setStep('TERMINALS'),
          variant: 'outline' as const,
        },
        primaryButton: {
          children: t('backoffice.terminalsDrawerConfirmOperationButton'),
          onClick: () => setIsProcessConfirmOpen(true),
          disabled: !isLinkConfigStepReady(),
        },
      };
    }

    return {
      primaryButton: {
        children: operationStatus === 'loading'
          ? t('backoffice.terminalsDrawerRegisterProcessingTitle')
          : t('backoffice.terminalsDrawerCloseButton'),
        onClick: handleClose,
        disabled: operationStatus === 'loading',
      },
    };
  };

  const getMergedLinkRows = () => {
    const payfacMap = Object.fromEntries((resolvedNames.payfacs ?? []).map((p) => [p.id, p.name]));
    const sellerMap = Object.fromEntries((resolvedNames.sellers ?? []).map((s) => [s.id, s.name]));
    return csvBatchRows.map((row) => ({
      ...row,
      payfacName: row.payfacId != null ? (payfacMap[row.payfacId] ?? null) : null,
      sellerName: row.sellerId != null ? (sellerMap[row.sellerId] ?? null) : null,
    }));
  };

  const stepperConfigByOperation: Record<string, { steps: { label: string }[]; stepIndex: Partial<Record<DrawerStep, number>> }> = {
    LINK: {
      steps: [
        { label: t('backoffice.terminalsDrawerStepOperationLabel') },
        { label: t('backoffice.terminalsDrawerStepTerminalsLabel') },
        { label: t('backoffice.terminalsDrawerStepLinkConfigLabel') },
        { label: t('backoffice.terminalsDrawerStepResultLabel') },
      ],
      stepIndex: { OPERATION: 1, TERMINALS: 2, LINK_CONFIG: 3, CONFIRM: 4 },
    },
    UNLINK: {
      steps: [
        { label: t('backoffice.terminalsDrawerStepOperationLabel') },
        { label: t('backoffice.terminalsDrawerStepTerminalsLabel') },
        { label: t('backoffice.terminalsDrawerStepResultLabel') },
      ],
      stepIndex: { OPERATION: 1, TERMINALS: 2, CONFIRM: 3 },
    },
    REGISTER: {
      steps: [
        { label: t('backoffice.terminalsDrawerStepOperationLabel') },
        { label: t('backoffice.terminalsDrawerStepModelLabel') },
        { label: t('backoffice.terminalsDrawerStepTerminalsLabel') },
        { label: t('backoffice.terminalsDrawerStepResultLabel') },
      ],
      stepIndex: { OPERATION: 1, MODEL: 2, TERMINALS: 3, CONFIRM: 4 },
    },
  };

  const getStepperConfig = () => {
    const config = stepperConfigByOperation[selectedOperation ?? ''] ?? stepperConfigByOperation.REGISTER;
    return {
      steps: config.steps,
      currentStep: config.stepIndex[step] ?? 1,
    };
  };

  const renderStep = () => {
    if (step === 'OPERATION') {
      return (
        <StepOperation
          selectedOperation={selectedOperation}
          onSelectOperation={(type) => {
            setSelectedOperation(type);
            setStep(type === 'REGISTER' ? 'MODEL' : 'TERMINALS');
          }}
        />
      );
    }

    if (step === 'MODEL') {
      return (
        <StepModel
          selectedModel={selectedModel}
          onSelectModel={(model) => {
            setSelectedModel(model);
            setStep('TERMINALS');
          }}
        />
      );
    }

    if (step === 'TERMINALS') {
      return (
        <StepTerminals
          operationType={selectedOperation!}
          selectedModel={selectedModel}
          serialNumbers={serialNumbers}
          csvFile={csvFile}
          csvBatchRows={getMergedLinkRows()}
          isResolvingNames={isResolvingNames}
          isDragging={isDragging}
          isSubmitting={operationStatus === 'loading'}
          fileInputRef={fileInputRef}
          serialInputProps={register('serialInput', {
            validate: (value) =>
              !serialNumbers.includes(value.trim()) ||
              t('backoffice.terminalsWizardSerialAlreadyAdded'),
          })}
          serialError={serialFormErrors.serialInput?.message}
          onAddSerial={handleAddSerial}
          onRemoveSerial={handleRemoveSerial}
          onRemoveCsvFile={handleRemoveCsvFile}
          onRemoveCsvRow={handleRemoveCsvRow}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onFileChange={handleFileChange}
        />
      );
    }

    if (step === 'LINK_CONFIG') {
      return (
        <StepLinkConfig
          allSerialNumbers={serialNumbers}
          selectedPayfac={selectedPayfac}
          linkAssignments={linkAssignments}
          onPayfacChange={setSelectedPayfac}
          onAssignmentsChange={setLinkAssignments}
          isSubmitting={operationStatus === 'loading'}
          {...stepLinkConfigData}
        />
      );
    }

    return (
      <StepConfirm
        operationType={selectedOperation!}
        serialNumbers={serialNumbers}
        selectedModel={selectedModel}
        operationStatus={operationStatus}
        operationResult={operationResult}
      />
    );
  };

  const { steps, currentStep } = getStepperConfig();

  return (
    <>
      <Drawer
        isOpen={isOpen}
        title={t('backoffice.terminalsDrawerTitle')}
        onClose={handleClose}
        footer={getFooter()}
        className={styles.drawer}
      >
        <div className={styles['stepper-wrapper']}>
          <Stepper
            steps={steps}
            currentStep={currentStep}
          />
        </div>

        {renderStep()}
      </Drawer>
      <ConfirmActionModal
        isOpen={isExitConfirmOpen}
        title={t('backoffice.terminalsDrawerExitConfirmTitle')}
        description={t('backoffice.terminalsDrawerExitConfirmDescription')}
        confirmLabel={t('backoffice.terminalsDrawerExitConfirmLeave')}
        cancelLabel={t('backoffice.terminalsDrawerExitConfirmStay')}
        onConfirm={handleConfirmExit}
        onClose={() => setIsExitConfirmOpen(false)}
      />
      <ConfirmActionModal
        isOpen={isProcessConfirmOpen}
        title={t('backoffice.terminalsDrawerProcessConfirmTitle')}
        description={t('backoffice.terminalsDrawerProcessConfirmDescription')}
        confirmLabel={t('backoffice.terminalsDrawerConfirmOperationButton')}
        cancelLabel={t('backoffice.terminalsWizardCancel')}
        onConfirm={() => {
          setIsProcessConfirmOpen(false);
          void handleConfirm();
        }}
        onClose={() => setIsProcessConfirmOpen(false)}
      />
    </>
  );
};
